// Helper for the top-bar "What's New" indicator. Pure functions — callers
// pass in the raw changelog markdown (typically fetched from /CHANGELOG.md
// at runtime) and this module reports whether the user has seen the
// latest release header.
//
// Grammar contract: release headers MUST match the exact pattern
//   ## v<major>.<minor>.<patch> — <YYYY-MM-DD>
// with an em-dash (—) between the version and the date, and a semver
// triple. This is enforced in CHANGELOG.md by the authoring convention
// and in HEADER_RE below. Anything else is ignored (including a level-1
// title or commentary paragraphs).

const HEADER_RE = /^##\s+v(\d+\.\d+\.\d+)\s+\u2014\s+\d{4}-\d{2}-\d{2}/m;
const LS_KEY = "chatfort.patchNotes.lastSeenVersion";

export function extractLatestVersion(md) {
  if (typeof md !== "string") return null;
  const m = md.match(HEADER_RE);
  return m ? m[1] : null;
}

// Semver compared as a tuple of numbers. Safe only because HEADER_RE
// already enforces three numeric segments. Null/unknown sorts lowest.
function cmpSemver(a, b) {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  const as = a.split(".").map(Number);
  const bs = b.split(".").map(Number);
  for (let i = 0; i < 3; i++) {
    const av = as[i] || 0, bv = bs[i] || 0;
    if (av !== bv) return av - bv;
  }
  return 0;
}

export function hasUnread(md) {
  const latest = extractLatestVersion(md);
  if (!latest) return false;
  let seen = null;
  try { seen = localStorage.getItem(LS_KEY); } catch { /* private-mode etc */ }
  return cmpSemver(latest, seen) > 0;
}

export function markSeen(md) {
  const latest = extractLatestVersion(md);
  if (!latest) return;
  try { localStorage.setItem(LS_KEY, latest); } catch { /* private-mode etc */ }
}

// Classify the user's relationship to the current changelog without
// mutating any storage. The caller decides what to do with each state:
//
//   "fresh-install" — first time ever loading the app. No lastSeenVersion
//                     in localStorage. Caller should silently markSeen()
//                     so a brand-new user isn't greeted by a panel for
//                     features they've never seen anything else of.
//   "up-to-date"    — stored matches latest. Caller does nothing.
//   "new-release"   — stored exists but is older than latest. Caller may
//                     auto-open the panel (slice 2 behavior).
//   "no-changelog"  — md is missing/empty/unparseable. Caller does nothing.
//
// Pure read of localStorage; no writes. Safe to call multiple times.
export function classifySeenState(md) {
  const latest = extractLatestVersion(md);
  if (!latest) return "no-changelog";
  let stored = null;
  try { stored = localStorage.getItem(LS_KEY); } catch { /* private-mode etc */ }
  if (!stored) return "fresh-install";
  if (stored === latest) return "up-to-date";
  return "new-release";
}
